import type { ReactNode } from "react";
import { createElement } from "react";

/* A missing value renders as a dash.
 *
 * docs/coding-standards.md: never fill a gap. Nobody having recorded a fact is
 * different from the fact being zero or empty, so a null still has to look
 * different from an empty cell, which would be indistinguishable from a
 * rendering fault.
 *
 * A dash, not a sentence. The app had at least five ways of saying the same
 * nothing (not recorded, unknown, none, nothing on file, never), which reads as
 * five different facts when it is one. The tooltip carries the meaning for
 * anyone who wants it, and a table of dashes scans in a way a table of
 * sentences does not. */
export function absent(what = "-", title = "Not on record"): ReactNode {
  return createElement("span", { className: "muted", title }, what);
}

/* A value the engine holds that still tells the reader nothing useful: an
   unconfirmed employment standing, a HubSpot check that found nothing or never
   ran. These render as a dash with the meaning on the tooltip, so a column of
   them reads as quiet rather than as a wall of "unknown" and "no" a person has
   to read before deciding it said nothing. */
export function nothingUseful(title: string): ReactNode {
  return absent("-", title);
}

/* A property's type in words. Unknown means no source has stated one (engine
   migration 260), so it reads as a quiet dash like any other value that says
   nothing, rather than as the word. */
export function propertyType(t: string | null | undefined): ReactNode {
  if (!t) return absent();
  if (t === "unknown") return nothingUseful("Type not known");
  const words = t.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A value, or the words that say there isn't one. */
export function orAbsent(v: string | number | null | undefined): ReactNode {
  if (v === null || v === undefined || v === "") return absent();
  return v;
}

/** City and state, or the words that say we have neither. */
export function place(city?: string | null, state?: string | null): ReactNode {
  const parts = [city, state].filter(Boolean);
  return parts.length ? parts.join(", ") : absent();
}

/* A date the lake holds, as a Date in the reader's own timezone.
 *
 * WHY THIS IS NOT new Date(iso)
 *
 * A Postgres `date` reaches the browser as "2026-09-15". The ECMAScript spec
 * parses a date-only string as UTC midnight, and toLocaleDateString then
 * renders it in the reader's zone, so everyone west of UTC sees the day before.
 * Buzze is in Phoenix, UTC-7, so every rep saw every date one day early.
 *
 * Found on 2026-09-15 by correcting a field and watching its own provenance row
 * come back dated the 14th while api.provenance returned the 15th. It was never
 * one row: shortDate is the funnel for every date on every screen, so it was
 * every source date, every run date and every project date in the app. That is
 * the "stale is never disguised as fresh" rule broken quietly, on the panel
 * whose whole job is answering how did you get my number.
 *
 * A timestamptz is unaffected, because it carries its own offset. Only the
 * date-only form is reinterpreted, so only that form is built by hand here. */
export function parseLakeDate(iso: string): Date | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A date, short, in the reader's own locale. Null says so. */
export function shortDate(iso?: string | null): ReactNode {
  if (!iso) return absent();
  const d = parseLakeDate(iso);
  if (!d) return absent("unreadable date");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/* A day for a "since" tag: "Oct 7" within the last year, "Oct 7, 2025" for
 * anything older or ahead. James, 2026-10-09: on a recent date the year is
 * noise. Built on parseLakeDate, as shortDate is, so the two agree on the day. */
export function recentDate(iso?: string | null, now: Date = new Date()): ReactNode {
  if (!iso) return absent();
  const d = parseLakeDate(iso);
  if (!d) return absent("unreadable date");
  const yearAgo = new Date(now);
  yearAgo.setFullYear(now.getFullYear() - 1);
  const recent = d > yearAgo && d.getTime() <= now.getTime() + 24 * 3600 * 1000;
  return d.toLocaleDateString(undefined, recent
    ? { month: "short", day: "numeric" }
    : { year: "numeric", month: "short", day: "numeric" });
}

/* How long ago, for a list somebody scans to find what is new.
 *
 * Built on parseLakeDate for the same reason shortDate is, so the two cannot
 * disagree about which calendar day a value falls on. A relative form that said
 * "yesterday" while the absolute form beside it said today would be worse than
 * having no relative form at all.
 *
 * It compares calendar days rather than elapsed milliseconds. 23 hours can span
 * two days and 25 hours can span one, and a reader asking how old something is
 * means days rather than multiples of 24 hours.
 *
 * Past 30 days it hands back to shortDate. Beyond a month "5 weeks ago" is
 * less use than the date itself, and switching over means the exact date is
 * still on the screen rather than behind a hover.
 */
export function relativeDate(iso?: string | null): ReactNode {
  if (!iso) return absent();
  const d = parseLakeDate(iso);
  if (!d) return absent("unreadable date");

  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(d)) / 86_400_000);

  // A clock skew or a date somebody typed ahead. Say the date rather than
  // inventing "in 2 days", which reads as a bug on a list of things that
  // happened.
  if (days < 0) return shortDate(iso);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "Last week";
  if (days <= 30) return `${Math.floor(days / 7)} weeks ago`;
  return shortDate(iso);
}

/** When a screen's data loaded. Never "a moment ago", which is a guess. */
export function loadedAt(ms: number | undefined): string {
  if (!ms) return "";
  return `Loaded at ${new Date(ms).toLocaleTimeString()}`;
}

export function count(n: number | null | undefined): string {
  return n === null || n === undefined ? "" : n.toLocaleString();
}

/* Roles as they read in a sentence. The lake stores property_manager, and a
   company holding two roles at one building reads "owner, property manager". */
export const roleWords = (roles: readonly string[] | null | undefined) =>
  (roles ?? []).map((r) => r.replace(/_/g, " ")).join(", ");

/* A moment in the reader's own clock, with its day: "Oct 23, 3:44 PM", and
 * the year when it is not this year. For a log, where two entries on one
 * day are told apart by the time. */
export function dateAndTime(iso?: string | null, now: Date = new Date()): ReactNode {
  if (!iso) return absent();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return absent("unreadable date");
  const day = d.toLocaleDateString(undefined, d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { year: "numeric", month: "short", day: "numeric" });
  return `${day}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
